// Timeline.tsx
import { Images } from 'Components/Images';
import StudentRenderer from 'Components/ModelRenderers/StudentRenderer';
import { correctRaidTerrain } from 'Components/RaidTerrainImage';
import RetainOverlayTrigger from 'Components/RetainOverlayTrigger';
import { TagBadge } from 'Components/TagBadge';
import { ICellData } from 'Interfaces/ICellData';
import IStyle from 'Interfaces/IStyle';
import ImageGallery from 'Modals/ImageGallery';
import ScheduleEntry, { BannerScheduleEntry, RaidScheduleEntry, TicketScheduleEntry } from 'Models/ScheduleEntry';
import Student from 'Models/Student';
import StudentMap from 'Models/StudentMap';
import moment from 'moment';
import React, { CSSProperties, useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';

const styles: IStyle = {
	pipe: {
		marginLeft: "0.6em",
		marginRight: "0.6em",
		// fontSize: "1.2em"
	},
}
interface TimelineProps {
	entries: ScheduleEntry[];
}
const map = new StudentMap();
const GROUP_ORDER = [
	"Calendar", "Banners", "Tickets1", "Tickets2", "Tickets3", "Weekly", 
	"Event", "Event1", "Event2", "Event3", 
	"Normals",
	"Hards",
	"Commissions",
	"Bounty",
	"Scrimmage",
	"Lessons",
	"EXP",
	// "Boost", "Boost1", "Boost2", "Boost3",
]; // Order of groups in the timeline
const GROUP_ORDER_BOTTOM = [
	"FRR", "PVP", "Calendar_End",
]; // Order of groups in the timeline
const DAY_WIDTH = 40; // Width of each day in pixels
const BannerScheduleRenderer = ({ entry }: { entry: BannerScheduleEntry }) => {
	return (
		<div className="timeline-entry">
			<Card className="entry text-pink" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
				border: "1px solid var(--pink)"
			}}>
				<Card.Header>
					<Card.Title className="responsive-font">
						{entry.start.format('YYYY-MMM-D')} - {entry.end.format('MMM-D')} <sub>({entry.duration}D)</sub>
					</Card.Title>
				</Card.Header>
				<Card.Body style={{textAlign: "center", alignContent: "center", whiteSpace: "nowrap", paddingTop: "0.2em", paddingBottom: "0.2em"}}>
					<span className="sticky-left">
						{entry.students.map((s, i) => (
							<RetainOverlayTrigger
								placement="top"
								trigger={['click', 'hover']}
								overlay={<StudentRenderer bare disableSchale model={Student.fromId(map.getIdFromUnknown(s))}></StudentRenderer>}
								>
									<span><Images.StudentIcon key={`${i}`} studentId={map.getIdFromUnknown(s).toString()} size={DAY_WIDTH+"px"} alt={s} /></span>
							</RetainOverlayTrigger>
						))}
					</span>
				</Card.Body>
			</Card>
		</div>
	);
};



function FRRTerrainTimelineImage(props: {devName?:string, name:string, terrain?: string, entry: RaidScheduleEntry}) {
	let {devName, terrain} = correctRaidTerrain(props);
	const {entry} = props;
	terrain = entry.terrain;
	//  style={{maxHeight: 200, overflowY: "hidden", margin: 0, padding: 0, textAlign: "center", alignContent: "center"}}
	return (
	<Card className="entry text-pink"  style={{
		width: DAY_WIDTH * entry.duration + 'px',
		margin: 0,
		border: "1px solid var(--pink)",
		maxHeight: 200, overflowY: "hidden", textAlign: "left", alignContent: "center"
	}}>
		<Card.Header style={{
			position: 'absolute',
			top: 0,
			width: "100%",
		}}>{entry.start.format('YYYY-MMM-D')} - {entry.end.format('MMM-D')} <sub>({entry.duration}D)</sub>
		</Card.Header>
		<Card.Img variant="bottom" src={`/img/Boss_Portrait_${devName}_Lobby.png`} />
		<Card.ImgOverlay>
			<Card.Body>
				<TagBadge pill pretext={`${entry.type} `} posttext={entry.season.toString()}>S-</TagBadge>
			</Card.Body>
		</Card.ImgOverlay>
		<Card.Footer style={{
			position: 'absolute',
			bottom: 0,
			width: "100%",
		}}>
			<Card.Text><TagBadge>{terrain}</TagBadge>{entry.name}
			&nbsp;{entry.def && <Images.Armor color={entry.def} size={35}/>}</Card.Text>
		</Card.Footer>
	</Card>
)
}

function RaidTerrainTimelineImage(props: {devName?:string, name:string, terrain?: string, entry: RaidScheduleEntry}) {
	let {devName, terrain} = correctRaidTerrain(props);
	const {entry} = props;
	terrain = entry.terrain;
	const cleanType = entry.type.replaceAll(/^GA\d+$/gi,"GA");
	const mode = parseInt(entry.type.match(/\d+$/gi)?.[0] ?? "0");
	return (
	<Card className="entry text-pink"  style={{
		width: DAY_WIDTH * entry.duration + 'px',
		margin: 0,
		border: "1px solid var(--pink)",
	}}>
		{<Card.Header>
			<Card.Title className="responsive-font">
				{entry.start.format('YYYY-MMM-D')} - {entry.end.format('MMM-D')} <sub>({entry.duration}D)</sub>
			</Card.Title>
		</Card.Header>}
			<Card.Img variant="bottom" src={`/img/Boss_Portrait_${devName}_Lobby.png`} />
		<Card.ImgOverlay>
			<Card.Body>
				<TagBadge pill pretext={`${cleanType} `} posttext={entry.season.toString()}>S-</TagBadge>
			</Card.Body>
		</Card.ImgOverlay>
		<Card.Footer>
			<Card.Text>
				<TagBadge>{terrain}</TagBadge>{entry.name}
				&nbsp;{entry.def && <Images.Armor color={entry.def} size={35}/>}
				{entry.def2 && <Images.Armor color={entry.def2} size={mode > 1 ? 35 : 25}/>}
				{entry.def3 && <Images.Armor color={entry.def3} size={mode > 2 ? 35 : 25}/>}
			</Card.Text>
		</Card.Footer>
	</Card>
)
}
const JFDScheduleRenderer = ({ entry }: { entry: RaidScheduleEntry }) => {
	return (
		<div className="timeline-entry" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<div className="timeline-content">
				<RaidTerrainTimelineImage entry={entry} name={"JointFiringDrill"}/>
			</div>
		</div>
	);
};

const FRRScheduleRenderer = ({ entry }: { entry: RaidScheduleEntry }) => {
	return (
		<div className="timeline-entry" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<div className="timeline-content" style={{
				width: DAY_WIDTH * entry.duration + 'px',
			}}>
				<FRRTerrainTimelineImage entry={entry} name={entry.name}/>
			</div>
		</div>
	);
};
const RaidScheduleRenderer = ({ entry }: { entry: RaidScheduleEntry }) => {
	return (
		<div className="timeline-entry" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<div className="timeline-content" style={{
				width: DAY_WIDTH * entry.duration + 'px',
			}}>
				<RaidTerrainTimelineImage entry={entry} name={entry.name}/>
			</div>
		</div>
	);
};


const TicketRenderer = ({ entry }: { entry: TicketScheduleEntry }) => {
	return (
		<div className={"timeline-entry ticket "+entry.group.toLowerCase()} style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<Card className="entry text-pink" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				height: "100%",
				border: "1px solid var(--pink)",
			}}>
				<Card.Header>
					<Card.Title className="responsive-font">
						<Images.Ticket /> {entry.name} {entry.reference.name} {entry.start.format('MMM-D')}{entry.duration > 3 && ` - ${entry.end.format('MMM-D')}`}
						{ entry.duration > 4 && <sub> ({entry.duration}D)</sub>} <Images.Ticket /> 
					</Card.Title>
				</Card.Header>
			</Card>
		</div>
	);
};

const ScheduleRenderer = ({ entry }: { entry: ScheduleEntry }) => {
	const style:CSSProperties = {
		objectFit: "cover",
		objectPosition: "center",
		maxWidth: DAY_WIDTH * entry.duration + 'px',
		maxHeight: "90px",
	}
	const timelineType = entry.type.replaceAll(/\d+$/gi,"");
	return (
		<div className="timeline-entry" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<Card className="entry text-pink" style={{
				width: DAY_WIDTH * entry.duration + 'px',
				height: "100%",
				border: "1px solid var(--pink)"
			}}>
				<Card.Header>
					<Card.Title className="responsive-font">
						{entry.start.format('MMM-D')}{entry.duration > 3 && ` - ${entry.end.format('MMM-D')}`}
						{ entry.duration > 4 && <sub> ({entry.duration}D)</sub>}
					</Card.Title>
				</Card.Header>
				<Card.Body style={{textAlign: "center", alignContent: "center", whiteSpace: "nowrap", paddingTop: 0, paddingBottom: 0}}>
					<span className="sticky-left">
						{!!entry.img && <ImageGallery
							title={`${timelineType}: ${entry.name}: ${entry.start.format('YYYY-MMM-D')} ~ ${entry.end.format('MMM-D')}`}
							width={null}
							style={style}
							srcs={[entry.img]}
						/>}
						{!!entry.img && !!entry.student && <span className="text-pink" style={styles.pipe}>●</span>}
						{!!entry.student && <Images.StudentIcon studentId={entry.student.getId().toString()} size={1.5*DAY_WIDTH} alt={entry.student.getDisplayName()} />}
					</span>
				</Card.Body>
			{(!!entry.img || !!entry.student) && <Card.ImgOverlay style={{width: 0}}>
				<Card.Body>
					<TagBadge pill>{timelineType}</TagBadge>
				</Card.Body>
			</Card.ImgOverlay>}
				<Card.Footer>
					<Card.Text>{entry.name}</Card.Text>
				</Card.Footer>
			</Card>
		</div>
	);
};

const CalendarRenderer = ({ entry }: { entry: ScheduleEntry }) => {
	return (
		<div className="timeline-entry" style={{
				width: DAY_WIDTH + 'px',
				marginLeft: entry.offset * DAY_WIDTH + 'px',
			}}>
			<Card className="entry text-pink" style={{
				width: DAY_WIDTH + 'px',
				border: "1px solid var(--pink)",
				boxShadow: Math.abs(Math.ceil(moment().diff(entry.start, "hours"))) <= 36 ? "inset 0 0 10px var(--pink)" : undefined
			}}>
				<Card.Header>
					<Card.Title className="responsive-font">
						{entry.start.format('D')}
					</Card.Title>
				</Card.Header>
			</Card>
		</div>
	);
};
class CalendarEntry extends ScheduleEntry {
	constructor({start, end, offset}) {
		super();
		this.start = start;
		this.end = end;
		this.offset = offset;
		this.type = "Calendar";
	}
	parse(row: ICellData[]): ScheduleEntry {
		throw new Error('Method not implemented.');
	}
}
const Timeline: React.FC<TimelineProps> = ({ entries }) => {
	// const scrollRef = useHorizontalScroll({speed: 8*window.innerWidth/window.innerHeight});
	// Group entries by type
	const groupedEntries: Record<string, ScheduleEntry[]> = useMemo(() => {
		const toret = entries.reduce((acc, entry) => {
			if (!acc[entry.group]) acc[entry.group] = [];
			acc[entry.group].push(entry);
			return acc;
		}, {} as Record<string, ScheduleEntry[]>);
		for (const group in toret) {
			toret[group].sort((a, b) => a.start.valueOf() - b.start.valueOf());
		}
		const earliestList = Object.values(toret).map(group => group[0]).sort((a, b) => a.start.valueOf() - b.start.valueOf());
		const earliest = earliestList.filter(l=>l.type !== "PVP")[0];
		const latestList = Object.values(toret).map(group => group[group.length-1]).sort((a, b) => b.end.valueOf() - a.end.valueOf());
		const latest = latestList.filter(l=>l.type !== "PVP")[0];
		if (!earliest || !latest) return toret;
		const earliestStart = earliest.start.clone().add(-((earliest.start.day()+7-2)%7), 'days');
		for (const group in toret) {
			toret[group][0].offset = toret[group][0].start.diff(earliestStart, 'days');
			// if (group === "PVP") {

			// }
			for (let i=1; i < toret[group].length; i++) {
				toret[group][i].offset = toret[group][i].start.diff(toret[group][i-1].end, 'days');
			}
		}
		toret["Calendar"] = [];
		for (let curr=moment(earliestStart); !curr.isAfter(latest.end); curr.add(1, 'days')) {
			toret["Calendar"].push(new CalendarEntry({
				start: curr.clone(),
				end: curr.clone().add(1, 'days'),
				offset: 0,
			}));
		}
		toret["Calendar"][0].offset = -0.5;
		toret["Calendar_End"] = toret["Calendar"];

		return toret;
	}, [entries]);
	const groups = [...new Set([...GROUP_ORDER.filter(group => groupedEntries[group]), ...Object.keys(groupedEntries).filter(g=>!GROUP_ORDER_BOTTOM.includes(g)).sort(), ...GROUP_ORDER_BOTTOM.filter(group => groupedEntries[group])])];
	return (
		<Row 
		onLoad={(e) => {e.currentTarget.focus();}}
		// ref={scrollRef} 
		className="timeline" style={{flexDirection: 'column'}}>
			<Col>
				{groups.map((type) => {
					const group = groupedEntries[type] || [];
					return (<Row key={type} className="timeline-row" style={{margin: 0, padding: 0}}>
						<div className="timeline-row-entries">
								{group
								.sort((a, b) => a.start.valueOf() - b.start.valueOf())
								.map((entry, index) => {
									if (entry instanceof BannerScheduleEntry) {
										return <BannerScheduleRenderer key={index} entry={entry as BannerScheduleEntry} />;
									} else if (entry.type === "JFD") {
										return <JFDScheduleRenderer key={index} entry={entry as RaidScheduleEntry} />;
									} else if (type === "Weekly") {
										return <RaidScheduleRenderer key={index} entry={entry as RaidScheduleEntry} />;
									} else if (entry.type === "FRR") {
										return <FRRScheduleRenderer key={index} entry={entry as RaidScheduleEntry} />;
									} else if (entry.type === "Calendar") {
										return <CalendarRenderer key={index} entry={entry as ScheduleEntry} />;
									} else if (entry.type === "Ticket") {
										return <TicketRenderer key={index} entry={entry as TicketScheduleEntry} />;
									}

									return <ScheduleRenderer key={index} entry={entry as RaidScheduleEntry} />;
								})
							}
						</div>
					</Row>);
				})}
			</Col>
		</Row>
	);
};

export default Timeline;
